import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { RESUME_CSS, buildResumeHTML } from '@/utils/resumeHtml';

// A4 in CSS pixels at 72 DPI: 595 × 842. WeasyPrint renders to exactly these dimensions,
// so we render the same HTML into a fixed-A4-sized inner div and CSS-scale it to fit
// whatever width the preview pane has. That guarantees pixel-perfect parity with the PDF.
const A4_WIDTH = 595;
const A4_HEIGHT = 842;

export function ResumePreview({ personalInfo, professionalSummary, workExperience, education, skills, projects, certifications }) {
  const wrapperRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);

  const html = useMemo(
    () => buildResumeHTML({ personalInfo, professionalSummary, workExperience, education, skills, projects, certifications }),
    [personalInfo, professionalSummary, workExperience, education, skills, projects, certifications]
  );

  // Defense-in-depth: every field is escaped in buildResumeHTML; DOMPurify strips
  // any tags/event-handlers in case a paste sneaks through.
  const sanitizedHtml = useMemo(() => DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }), [html]);
  const htmlProps = useMemo(() => ({ __html: sanitizedHtml }), [sanitizedHtml]);

  useEffect(() => {
    const measure = () => {
      const w = wrapperRef.current?.clientWidth || 0;
      setScale(w > 0 ? w / A4_WIDTH : 1);
      setContentHeight(innerRef.current?.scrollHeight || 0);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    if (innerRef.current) ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, [sanitizedHtml]);

  const needsPage2 = contentHeight > A4_HEIGHT + 4;
  const scaledHeight = A4_HEIGHT * scale;

  return (
    <div data-testid="resume-preview-wrapper" ref={wrapperRef} className="space-y-3">
      <style>{RESUME_CSS}</style>

      {/* Page 1 */}
      <div
        data-testid="resume-preview"
        className="relative bg-white shadow-lg overflow-hidden"
        style={{ width: '100%', height: `${scaledHeight}px` }}
      >
        <div
          ref={innerRef}
          style={{ width: `${A4_WIDTH}px`, height: `${A4_HEIGHT}px`, transform: `scale(${scale})`, transformOrigin: 'top left', overflow: 'hidden' }}
          dangerouslySetInnerHTML={htmlProps}
        />
        <div className="absolute bottom-2 right-3 text-[10px] font-bold tracking-widest text-slate-400 select-none">PAGE 1</div>
      </div>

      {/* Page 2 (only if content overflows) */}
      {needsPage2 && (
        <div
          data-testid="resume-preview-page-2"
          className="relative bg-white shadow-lg overflow-hidden"
          style={{ width: '100%', height: `${scaledHeight}px` }}
        >
          <div
            style={{ width: `${A4_WIDTH}px`, transform: `scale(${scale})`, transformOrigin: 'top left' }}
          >
            <div style={{ marginTop: `-${A4_HEIGHT}px` }} dangerouslySetInnerHTML={htmlProps} />
          </div>
          <div className="absolute bottom-2 right-3 text-[10px] font-bold tracking-widest text-slate-400 select-none">PAGE 2</div>
        </div>
      )}
    </div>
  );
}
